import moment from 'moment'
import { enumNarrow } from '../../helpers/enumGuard'
import { Category, PREDICTABLE_SKU_TYPES, SkuTypes } from '../../interfaces/bbc'
import {
  ClientAmendmentType,
  ClientInfoStatus,
  TermLoanOveradvanceType,
  ClientIndustries,
} from '../../interfaces/client'
import {
  AGGREGATION_TYPES,
  LOAN_TYPES,
  OVERPAYABLE_INTERVAL_VALUES,
  VENDOR_TYPE_VALUES,
} from '../client'
import { FilterFieldType, IFilter } from './types'

export enum ClientActivityFilterFields {
  TYPE = 'type',
  AMOUNT = 'amount',
  TERM_LOAN_BALANCE_ENDING = 'termLoanBalanceEnding',
  DESCRIPTION = 'description',
  LOAN_BALANCE_ENDING = 'loanBalanceEnding',
  TOTAL_LOAN_BALANCE_ENDING = 'totalLoanBalanceEnding',
}

/** Cast-free narrower for ClientActivityFilterFields (see common/helpers/enumGuard). */
export const asClientActivityFilterFields = enumNarrow(ClientActivityFilterFields).as

export const CLIENT_ACTIVITY_FILTER_ORDER = [
  ClientActivityFilterFields.TYPE,
  ClientActivityFilterFields.AMOUNT,
  ClientActivityFilterFields.TERM_LOAN_BALANCE_ENDING,
  ClientActivityFilterFields.DESCRIPTION,
  ClientActivityFilterFields.LOAN_BALANCE_ENDING,
  ClientActivityFilterFields.TOTAL_LOAN_BALANCE_ENDING,
]

export const CLIENT_ACTIVITY_FILTER_TITLE_OVERRIDES: Record<ClientActivityFilterFields, string> = {
  [ClientActivityFilterFields.TYPE]: 'Activity Type',
  [ClientActivityFilterFields.AMOUNT]: 'Amount',
  [ClientActivityFilterFields.TERM_LOAN_BALANCE_ENDING]: 'Term Loan',
  [ClientActivityFilterFields.DESCRIPTION]: 'Description',
  [ClientActivityFilterFields.LOAN_BALANCE_ENDING]: 'Revolver',
  [ClientActivityFilterFields.TOTAL_LOAN_BALANCE_ENDING]: 'Total Balance',
}

export const CLIENT_ACCOUNT_ACTIVITY_LIST_FILTERS_CONFIG = [
  {
    field: 'recordDate',
    dbField: 'record_date',
    type: FilterFieldType.Date,
    isSortable: true,
    title: 'Date',
    alias: 'activity',
    mobileRender: 'secondaryNumber',
    isExternalField: true,
  },
  {
    field: 'type',
    type: FilterFieldType.List,
    isMultiple: true,
    isSortable: true,
    options: [
      {
        value: 'all',
        label: 'All',
      },
      ...Object.values(AGGREGATION_TYPES).map((value) => ({
        value,
        label: value,
      })),
    ],
    title: 'Type',
    alias: 'activity',
    mobileRender: 'secondaryText',
  },
  {
    field: 'description',
    type: FilterFieldType.Autocomplete,
    isSortable: true,
    placeholder: 'Description',
    title: 'Description',
    alias: 'activity',
    isMultiple: true,
    mobileRender: 'mainText',
  },
  {
    field: 'amount',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Amount',
    alias: 'activity',
    mobileRender: 'primaryNumber',
    align: 'right',
  },
  {
    field: 'loanBalanceEnding',
    dbField: 'loan_balance_ending',
    type: FilterFieldType.Amount,
    align: 'right',
    isSortable: false,
    title: 'Revolver',
    alias: 'activity',
  },
  {
    field: 'termLoanBalanceEnding',
    dbField: 'term_loan_balance_ending',
    type: FilterFieldType.Amount,
    align: 'right',
    isSortable: false,
    title: 'Term Loan',
    alias: 'activity',
  },
  {
    field: 'totalLoanBalanceEnding',
    dbField: 'total_loan_balance_ending',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Total Balance',
    align: 'right',
  },
  {
    field: 'originalMemo',
    dbField: 'original_memo',
    type: FilterFieldType.Text,
    excludeColumn: true,
    alias: 'activity',
  },
  {
    field: 'checkNumber',
    dbField: 'check_number',
    type: FilterFieldType.Text,
    alias: 'checkData',
    excludeColumn: true,
  },
  {
    field: 'action',
    type: FilterFieldType.Empty,
  },
  {
    type: FilterFieldType.QuickFilter,
    field: 'quickFilter',
    quickFilters: [
      {
        title: 'Today',
        filters: {
          recordDateFrom: moment().format('YYYY-MM-DD'),
          recordDateTo: moment().format('YYYY-MM-DD'),
        },
      },
      {
        title: 'Fundings',
        filters: {
          type: [AGGREGATION_TYPES.funding],
        },
      },
      {
        title: 'Collections',
        filters: {
          type: [AGGREGATION_TYPES.check, AGGREGATION_TYPES.wire, AGGREGATION_TYPES.adjustment],
        },
      },
      {
        title: 'Interest & Fees',
        filters: {
          type: [
            AGGREGATION_TYPES.fee,
            AGGREGATION_TYPES.wireFee,
            AGGREGATION_TYPES.facilityFee,
            AGGREGATION_TYPES.lockboxFee,
            AGGREGATION_TYPES.terminationFee,
            AGGREGATION_TYPES.interestAmountMonthly,
          ],
        },
      },
    ],
  },
] as const satisfies IFilter[]

export const COMPLIANCE_STATUS_LIST_FILTERS_CONFIG = [
  {
    field: 'documentType',
    type: FilterFieldType.Text,
    title: 'Document Type',
    isSortable: false,
    excludeFilter: true,
  },
  {
    field: 'status',
    type: FilterFieldType.List,
    title: 'Status',
    isSortable: false,
    options: [
      { value: 'all', label: 'All' },
      { value: 'current', label: 'Current' },
      { value: 'due_soon', label: 'Near Due' },
      { value: 'overdue', label: 'Overdue' },
      { value: 'file_missing', label: 'File Missing' },
    ],
  },
  {
    field: 'lastUpdated',
    type: FilterFieldType.Date,
    title: 'Last Updated',
    isSortable: false,
    excludeFilter: true,
    align: 'right',
  },
  {
    field: 'nextDue',
    type: FilterFieldType.Date,
    title: 'Next Due',
    isSortable: false,
    excludeFilter: true,
    align: 'right',
  },
  {
    field: 'action',
    type: FilterFieldType.Empty,
    title: 'Actions',
  },
] as const satisfies IFilter[]

export const CLIENT_TERM_LOANS_LIST_FILTERS_CONFIG = [
  {
    field: 'boxLink',
    dbField: 'box_link',
    type: FilterFieldType.Text,
    isSortable: true,
    placeholder: 'Link to contract',
    title: 'Box Link',
  },
  {
    field: 'collateralName',
    dbField: 'collateral_name',
    type: FilterFieldType.Text,
    isSortable: true,
    placeholder: 'Collateral Name',
    title: 'Collateral Name',
  },
  {
    field: 'loanAmount',
    dbField: 'loan_amount',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Loan Amount',
  },
  {
    field: 'amortization',
    dbField: 'amortization',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Amortization',
  },
  {
    field: 'term',
    dbField: 'term',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Term',
  },
  {
    field: 'rate',
    dbField: 'rate',
    type: FilterFieldType.Percent,
    isCustom: true,
    title: 'Rate',
  },
  {
    field: 'baseRate',
    dbField: 'base_rate',
    type: FilterFieldType.Percent,
    isSortable: true,
    title: 'Floor',
  },
  {
    field: 'abovePrime',
    dbField: 'above_prime',
    type: FilterFieldType.Percent,
    isSortable: true,
    title: 'Above Prime',
  },
  {
    field: 'closingDate',
    dbField: 'closing_date',
    type: FilterFieldType.Date,
    isSortable: true,
    title: 'Closing Date',
  },
  {
    field: 'terminationDate',
    dbField: 'termination_date',
    type: FilterFieldType.Date,
    isSortable: true,
    title: 'Termination Date',
  },
  {
    field: 'closingFeePercent',
    dbField: 'closing_fee_percent',
    type: FilterFieldType.Percent,
    isSortable: true,
    title: 'Facility Fee Percent',
  },
  {
    field: 'closingFeeAmount',
    dbField: 'closing_fee_amount',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Facility Fee Amount',
  },
  {
    field: 'miscFees',
    dbField: 'misc_fees',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Misc Fees',
  },
  {
    field: 'wireFee',
    dbField: 'wire_fee',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Wire Fee',
  },
  {
    field: 'action',
    type: FilterFieldType.Empty,
  },
] as const satisfies IFilter[]

export const CLIENT_TERM_LOAN_AMORTIZATION_LIST_FILTERS_CONFIG = [
  {
    field: 'recordDate',
    dbField: 'record_date',
    type: FilterFieldType.Date,
    isSortable: true,
    title: 'Date',
  },
  {
    field: 'interest',
    dbField: 'interest',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Interest',
  },
  {
    field: 'principal',
    dbField: 'principal',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Principal',
  },
  {
    field: 'loanBalance',
    dbField: 'loan_balance',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Balance',
  },
  {
    field: 'action',
    type: FilterFieldType.Empty,
  },
] as const satisfies IFilter[]

export const CLIENT_AR_ELIGIBILITY_LIST_FILTERS_CONFIG = [
  {
    field: 'debtor',
    type: FilterFieldType.Autocomplete,
    isSortable: true,
    placeholder: 'Customer',
    title: 'Customer',
    alias: 'debtorClientEligibility',
  },
  {
    field: 'eligible',
    dbField: 'eligible',
    title: 'Eligible',
    alias: 'debtorClientEligibility',
    type: FilterFieldType.Boolean,
    isSortable: true,
    options: [
      {
        value: 'true',
        label: 'Yes',
      },
      {
        value: 'false',
        label: 'No',
      },
    ],
  },
  {
    field: 'precedent',
    type: FilterFieldType.Empty,
    title: 'Suggestion',
    align: 'left',
  },
  {
    field: 'category',
    dbField: 'category',
    type: FilterFieldType.List,
    isSortable: true,
    options: [
      {
        value: 'all',
        label: 'All',
      },
      ...Object.keys(Category).map((category) => ({
        value: category,
        label: category,
      })),
    ],
    title: 'Category',
    alias: 'debtorClientEligibility',
  },
  {
    field: 'lastAmount',
    dbField: '"lastAmount"',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Current amount',
    alias: 'clientArSummarySubQueryTotal',
    align: 'left',
  },

  {
    field: 'creditLimit',
    dbField: 'credit_limits',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Credit limit',
    align: 'right',
  },

  {
    field: 'concentrationLimit',
    dbField: 'concentration_limit',
    type: FilterFieldType.Percent,
    isSortable: true,
    title: 'Conc. limit',
    align: 'right',
  },
  {
    field: 'lastActive',
    dbField: '"lastActive"',
    type: FilterFieldType.Date,
    isSortable: true,
    title: 'Last active',
    alias: 'clientArSummarySubQuery',
    align: 'left',
    isExternalField: true,
  },

  {
    field: 'notesReason',
    dbField: 'notes_reason',
    type: FilterFieldType.Text,
    isSortable: true,
    placeholder: 'Notes',
    title: 'Notes',
  },
  {
    type: FilterFieldType.QuickFilter,
    field: 'quickFilter',
    quickFilters: [
      {
        title: 'New Customers',
        filters: {
          eligible: [null] as string[] | null[],
        },
      },
    ],
  },
] as const satisfies IFilter[]

export const CLIENT_AP_ELIGIBILITY_LIST_FILTERS_CONFIG = [
  {
    field: 'creditor',
    type: FilterFieldType.Autocomplete,
    isSortable: true,
    placeholder: 'Vendor',
    title: 'Vendor',
    alias: 'creditorClient',
  },
  {
    field: 'type',
    type: FilterFieldType.List,
    title: 'Type',
    isSortable: true,
    placeholder: 'Type',
    options: VENDOR_TYPE_VALUES,
    alias: 'creditorClient',
  },
  {
    field: 'priority',
    type: FilterFieldType.Empty,
    isSortable: false,
    placeholder: 'Priority',
    title: 'Priority',
  },

  {
    field: 'lastAmount',
    dbField: '"lastAmount"',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Current amount',
    alias: 'clientApSummarySubQueryTotal',
    align: 'right',
  },

  {
    field: 'priorityPayable',
    dbField: 'priority_payable',
    type: FilterFieldType.List,
    isSortable: true,
    options: OVERPAYABLE_INTERVAL_VALUES,
    title: 'Aging threshold',
    align: 'left',
  },

  {
    field: 'lastActive',
    dbField: '"lastActive"',
    type: FilterFieldType.Date,
    isSortable: true,
    title: 'Last active',
    align: 'left',
    alias: 'clientApSummarySubQuery',
    isExternalField: true,
  },

  {
    field: 'notesReason',
    dbField: 'notes',
    type: FilterFieldType.Text,
    isSortable: true,
    placeholder: 'Notes',
    title: 'Notes',
  },
  {
    type: FilterFieldType.QuickFilter,
    field: 'quickFilter',
    fields: [
      {
        field: 'isPriorityPayable',
        type: FilterFieldType.Text,
      },
    ],
    quickFilters: [
      {
        title: 'Priority Vendors',
        isHidden: true,
        filters: {
          isPriorityPayable: 'true',
        },
      },
    ],
  },
] as const satisfies IFilter[]

export const ELIGIBILITY_NOTES_FILTERS_CONFIG = [
  {
    field: 'eligible',
    type: FilterFieldType.List,
    isSortable: true,
    options: [
      {
        value: 'all',
        label: 'All',
      },
      {
        value: true,
        label: 'Eligible',
      },
      {
        value: false,
        label: 'Ineligible',
      },
    ],
    title: 'Eligible',
    alias: 'masterInventory',
  },
  {
    field: 'notes',
    type: FilterFieldType.Text,
    isSortable: true,
    placeholder: 'Notes',
    title: 'Notes',
    alias: 'masterInventory',
  },
] as const satisfies IFilter[]

export const CLIENT_MASTER_INVENTORY_LIST_FILTERS_CONFIG = [
  {
    field: 'recordDate',
    dbField: 'record_date',
    type: FilterFieldType.Date,
    isSortable: true,
    placeholder: 'First Appeared',
    title: 'First Appeared',
    alias: 'borrowingBase',
  },
  {
    field: 'description',
    type: FilterFieldType.Text,
    isSortable: true,
    placeholder: 'Description',
    title: 'Description',
    alias: 'masterInventory',
  },
  {
    field: 'sku',
    type: FilterFieldType.Text,
    isSortable: true,
    placeholder: 'SKU',
    title: 'SKU',
    alias: 'masterInventory',
  },
  {
    field: 'type',
    type: FilterFieldType.List,
    dbField: 'type',
    isSortable: true,
    options: [
      {
        value: 'all',
        label: 'All',
      },
      {
        value: '',
        label: 'New',
      },
      {
        value: SkuTypes.Finished,
        label: SkuTypes.Finished,
      },
      {
        value: SkuTypes.InProcess,
        label: SkuTypes.InProcess,
      },
      {
        value: SkuTypes.Raw,
        label: SkuTypes.Raw,
      },
      {
        value: SkuTypes.Packaging,
        label: SkuTypes.Packaging,
      },
      {
        value: SkuTypes.Other,
        label: SkuTypes.Other,
      },
    ],
    title: 'Type',
    alias: 'masterInventory',
    isMultiple: true,
  },
  {
    field: 'brandCategory',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Category',
    align: 'left',
    dbField: 'brand_category',
    alias: 'masterInventory',
  },
  {
    field: 'lastAmount',
    dbField: '"lastAmount"',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Current amount',
    align: 'left',
    alias: 'clientInventorySubQueryTotal',
  },
  {
    field: 'lastActive',
    dbField: '"lastActive"',
    type: FilterFieldType.Date,
    isSortable: true,
    title: 'Last active',
    align: 'left',
    alias: 'clientInventorySubQuery',
    isExternalField: true,
  },
  ...ELIGIBILITY_NOTES_FILTERS_CONFIG,

  {
    // Filter-only: the suggestion is rendered inside the Type cell, not in a column of its own,
    // so this exists purely to isolate the review queue. `excludeColumn` is what keeps the
    // header and the body aligned.
    field: 'predictedType',
    dbField: 'predicted_type',
    type: FilterFieldType.List,
    excludeColumn: true,
    isMultiple: true,
    options: PREDICTABLE_SKU_TYPES.map((type) => ({ value: type, label: type })),
    title: 'Suggested type',
    placeholder: 'Suggested type',
    alias: 'masterInventory',
  },
  {
    type: FilterFieldType.QuickFilter,
    field: 'quickFilter',
    quickFilters: [
      {
        title: 'New',
        filters: {
          type: [''],
        },
      },
      {
        title: 'Suggested',
        filters: {
          predictedType: [...PREDICTABLE_SKU_TYPES],
        },
      },
      {
        title: 'Finished Goods',
        filters: {
          type: [SkuTypes.Finished],
        },
      },
      {
        title: 'Not Finished Goods',
        filters: {
          type: ['', SkuTypes.InProcess, SkuTypes.Raw, SkuTypes.Packaging, SkuTypes.Other],
        },
      },
    ],
  },
] as const satisfies IFilter[]

export const CLIENT_INVENTORY_ELIGIBILITY_LIST_FILTERS_CONFIG = [
  {
    field: 'label',
    dbField: 'label',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Label',
    align: 'left',
    alias: 'rules',
  },
  {
    field: 'field',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Column Name',
    align: 'left',
    alias: 'rules',
  },
  {
    field: 'ineligibleReason',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Reason',
    align: 'left',
    alias: 'rules',
  },
  {
    field: 'operator',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Operator',
    alias: 'rules',
  },
  {
    field: 'value',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Value',
    align: 'left',
    alias: 'rules',
  },
  {
    field: 'eligible',
    type: FilterFieldType.List,
    isSortable: true,
    align: 'left',
    options: [
      {
        value: 'all',
        label: 'All',
      },
      {
        value: true,
        label: 'Eligible',
      },
      {
        value: false,
        label: 'Ineligible',
      },
    ],
    title: 'Eligible',
    alias: 'rules',
  },
  {
    field: 'overrides',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Override',
    alias: 'rules',
  },
  {
    field: 'action',
    type: FilterFieldType.Empty,
    title: 'Action',
    align: 'right',
  },
] as const satisfies IFilter[]

export const CLIENT_INVENTORY_LOCATIONS_FILTERS_CONFIG = [
  {
    field: 'location',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Location',
  },
  {
    field: 'mappedLocation',
    dbField: 'mapped_location',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Location Alias',
  },
  {
    field: 'entityName',
    dbField: 'entity_name',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Vendor',
  },
  {
    field: 'warehouseWaiverLink',
    dbField: 'warehouse_waiver_link',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Warehouse Waiver',
  },
] as const satisfies IFilter[]

export const CLIENT_PARTICIPATION_FILTERS_CONFIG = [
  {
    field: 'participant',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Participant',
    dbField: 'participant',
    alias: 'participation',
  },
  {
    field: 'loanExposure',
    type: FilterFieldType.Percent,
    isSortable: true,
    title: 'Loan Exposure',
    dbField: 'loan_exposure',
    alias: 'participation',
  },
  {
    field: 'interestOverride',
    type: FilterFieldType.Percent,
    isSortable: true,
    title: 'Interest Override',
    dbField: 'interest_override',
    alias: 'participation',
  },
  {
    field: 'contractDate',
    type: FilterFieldType.Date,
    isSortable: true,
    title: 'Contract Date',
    dbField: 'contract_date',
    alias: 'participation',
  },
  {
    field: 'action',
    type: FilterFieldType.Empty,
    title: 'Action',
    align: 'right',
  },
] as const satisfies IFilter[]

export const CLIENT_LOAN_LEDGER_FILTERS_CONFIG = [
  {
    field: 'recordDate',
    type: FilterFieldType.Date,
    isSortable: true,
    title: 'Date',
    alias: 'loan_balances',
    // Renders as a date range picker in the section header (left of the View dropdown)
    // instead of inside the filter popover, matching the Account Activity view it shares
    // that dropdown with. FilterContainer picks this up; FilterMenu drops the field so the
    // control lives in exactly one place.
    isExternalField: true,
  },
  {
    field: 'loanBalancePrev',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Starting Balance',
    alias: 'loan_balances',
  },
  {
    field: 'funds_out',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Fundings & Pass-throughs',
    dbField: '(loan_balances.funding + loan_balances.passThroughs)',
  },
  {
    field: 'funds_in',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Collections & Adjustments',
    dbField:
      '(loan_balances.collections - loan_balances.adjustments - loan_balances.termLoanPayments)',
  },
  {
    field: 'total_fees',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Interest & Fees',
    dbField:
      '(loan_balances.fees + loan_balances.interest_fees + loan_balances.term_loan_interest_fees + loan_balances.facility_fees + loan_balances.interest_amount_monthly)',
  },
  {
    field: 'loanBalance',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Ending Balance',
    alias: 'loan_balances',
  },
  {
    field: 'dwightExposure',
    type: FilterFieldType.Amount,
    title: 'Dwight NFE',
    align: 'right',
    excludeFilter: true,
  },
  {
    field: 'interestRateDaily',
    type: FilterFieldType.Percent,
    isSortable: true,
    title: 'Interest Rate',
    alias: 'loan_balances',
  },
  {
    field: 'interestAmountDaily',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Accrued Interest',
    alias: 'loan_balances',
  },
  {
    // Computed per-day line utilization (revolver ending balance ÷ net availability),
    // so a series of ledger rows reveals the change in utilization over time.
    // Display-only: not filterable or sortable since it isn't a stored column.
    field: 'utilization',
    type: FilterFieldType.Percent,
    isSortable: false,
    excludeFilter: true,
    title: 'Utilization',
    align: 'right',
  },
] as const satisfies IFilter[]

export const CLIENT_PENDING_CASH_DOMINION_LIST_FILTERS_CONFIG = [
  {
    field: 'entity_name',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Customer',
  },
  {
    field: 'record_date',
    type: FilterFieldType.Date,
    isSortable: true,
    title: 'Last Transaction',
    isExternalField: true,
  },
  {
    field: 'category',
    type: FilterFieldType.Empty,
    isSortable: false,
    title: 'Category',
  },
  {
    field: 'amount',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Total Amount (Last 90 days)',
  },
  {
    field: 'actions',
    type: FilterFieldType.Empty,
  },
] as const satisfies IFilter[]

export const PENDING_CASH_DOMINION_LIST_ALL_FILTERS_CONFIG = [
  {
    field: 'client_name',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Client',
  },
  {
    field: 'entity_name',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Entity',
  },
  {
    field: 'record_date',
    type: FilterFieldType.Date,
    isSortable: true,
    title: 'Last Transaction',
  },
  {
    field: 'amount',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Total Amount(Last 90 Days)',
  },
  {
    field: 'actions',
    type: FilterFieldType.Empty,
  },
  {
    type: FilterFieldType.QuickFilter,
    field: 'quickFilter',
    fields: [
      {
        field: 'filterWaived',
        type: FilterFieldType.Text,
      },
    ],
    quickFilters: [
      {
        title: 'Open',
        filters: {
          filterWaived: 'true',
        },
        isHidden: true,
      },
    ],
  },
] as const satisfies IFilter[]

export const AMENDMENTS_QUEUE_FILTERS_CONFIG = [
  {
    field: 'expand',
    title: '',
    type: FilterFieldType.Empty,
  },
  {
    field: 'amendmentType',
    dbField: 'amendment_type',
    type: FilterFieldType.List,
    alias: 'amendment',
    options: Object.values(ClientAmendmentType).map((type) => ({
      value: type,
      label: type,
    })),
    isSortable: true,
    title: 'Amendment Type',
  },
  {
    // Box icon: links to the amendment's legal document, or — once the
    // amendment analysis is complete — to the analysis's Box folder.
    field: 'document',
    title: '',
    type: FilterFieldType.Empty,
  },
  {
    field: 'createdAt',
    dbField: 'created_at',
    alias: 'amendment',
    type: FilterFieldType.Date,
    isSortable: true,
    title: 'Created Date',
    align: 'left',
  },
  {
    field: 'effectiveDate',
    dbField: 'effective_date',
    alias: 'amendment',
    type: FilterFieldType.Date,
    isSortable: true,
    title: 'Effective Date',
    align: 'left',
  },
  {
    field: 'summaryOfChanges',
    type: FilterFieldType.Text,
    isSortable: false,
    title: 'Summary of Changes',
    align: 'left',
  },
  {
    field: 'action',
    title: 'Action',
    type: FilterFieldType.Empty,
  },
] as const satisfies IFilter[]

export const PORTFOLIO_REVIEW_METRICS_LIST_FILTERS_CONFIG = [
  {
    field: 'lineTurn',
    type: FilterFieldType.Number,
    isSortable: true,
    title: 'Line Turn',
    aggColumn: true,
    dbField: 'MAX(collections.collections_t3) / 3 / NULLIF(MAX(balance_avg.balance_avg), 0)',
  },
  {
    field: 'arAdvance',
    type: FilterFieldType.Percent,
    isSortable: true,
    title: 'AR Adv.',
    alias: 'clientInfo',
    dbField: 'ar_advance',
  },
  {
    field: 'inventoryAdvanceRateCost',
    type: FilterFieldType.Percent,
    isSortable: true,
    title: 'Inventory Adv.',
    alias: 'clientInfo',
    dbField: 'inventory_advance_rate_cost',
  },
  {
    field: 'nolv',
    type: FilterFieldType.Percent,
    isSortable: true,
    title: 'NOLV',
    alias: 'clientInfo',
    dbField: 'nolv',
  },
  {
    field: 'minimumLineAmount',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Minimum',
    alias: 'clientInfo',
    dbField: 'minimum_line_amount',
  },
  {
    field: 'maxLineAmount',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Max Line Amount',
    alias: 'clientInfo',
    dbField: 'max_line_amount',
  },
  {
    field: 'clientStatus',
    dbField: 'client_status',
    type: FilterFieldType.List,
    isMultiple: true,
    isSortable: true,
    options: [
      { value: 'all', label: 'All' },
      { value: ClientInfoStatus.Active, label: 'Active' },
      { value: ClientInfoStatus.Current, label: 'Current' },
      { value: ClientInfoStatus.DueDiligence, label: 'Due Diligence' },
      { value: ClientInfoStatus.Past, label: 'Past' },
      { value: ClientInfoStatus.Archived, label: 'Past Archived' },
    ],
    placeholder: 'Status',
    title: 'Status',
    excludeColumn: true,
  },
] as const satisfies IFilter[]

export const PORTFOLIO_REVIEW_LIST_FILTERS_CONFIG = [
  {
    field: 'clients',
    dbField: 'client_name',
    type: FilterFieldType.Autocomplete,
    isSortable: true,
    placeholder: 'Client',
    title: 'Client',
    alias: 'clientInfo',
  },
  {
    field: 'loanType',
    type: FilterFieldType.List,
    isSortable: true,
    isMultiple: true,
    placeholder: 'Deal Type',
    title: 'Deal Type',
    alias: 'clientInfo',
    dbField: 'loan_type',
    options: Object.values(LOAN_TYPES).map((value) => ({
      value,
      label: value,
    })),
    excludeColumn: true,
  },
  {
    field: 'industry',
    type: FilterFieldType.List,
    isSortable: true,
    isMultiple: true,
    placeholder: 'Industry',
    title: 'Industry',
    alias: 'clientInfo',
    dbField: 'industry',
    options: Object.values(ClientIndustries).map((value) => ({
      value,
      label: value,
    })),
    excludeColumn: true,
  },
  {
    field: 'currentAmount',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Loan Balance',
    aggColumn: true,
    dbField:
      '(COALESCE(MAX(loanBalances.loan_balance),0)+ COALESCE(MAX(term_loan_balances.term_loan_balance),0))',
  },
  {
    field: 'utilization',
    type: FilterFieldType.Percent,
    isSortable: true,
    title: 'Utilization',
    aggColumn: true,
    dbField: `MAX(CASE WHEN "clientInfo".client_status = '${ClientInfoStatus.Current}' THEN loanBalances.loan_balance ELSE 0 END)/NULLIF(MAX(CASE WHEN "clientInfo".client_status = '${ClientInfoStatus.Current}' THEN clientCollaterals.final_net_availability ELSE 0 END), 0)`,
  },
  {
    field: 'risk',
    type: FilterFieldType.Number,
    isSortable: true,
    title: 'Risk Rating',
    aggColumn: true,
    dbField: 'MAX(riskRatingRawScores.risk_rating)',
  },
  {
    type: FilterFieldType.QuickFilter,
    field: 'quickFilter',
    fields: [
      {
        field: 'userId',
        type: FilterFieldType.List,
      },
      {
        field: 'lineTurn',
        type: FilterFieldType.Number,
      },
      {
        field: 'currentAmount',
        type: FilterFieldType.Amount,
      },
      {
        field: 'utilization',
        type: FilterFieldType.Percent,
      },
      {
        field: 'risk',
        type: FilterFieldType.Number,
      },
    ],
    quickFilters: [
      {
        title: 'LI/OA Candidates',
        filters: {
          riskMin: 4.75,
          lineTurnMin: 0.8,
          currentAmountMin: 750000,
          utilizationMin: 70,
        },
      },
    ],
  },
] as const satisfies IFilter[]

export const TERM_LOANS_AND_OVERADVANCES_FILTERS_CONFIG = [
  {
    field: 'clientName',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Client Name',
  },
  {
    field: 'type',
    dbField: 'type',
    type: FilterFieldType.List,
    isMultiple: true,
    isSortable: true,
    title: 'Type',
    // Equipment Loans never appear in the portfolio-wide table, so they are
    // excluded from this filter's options.
    options: Object.values(TermLoanOveradvanceType)
      .filter((type) => type !== TermLoanOveradvanceType.EquipmentLoan)
      .map((type) => ({
        value: type,
        label: type,
      })),
  },
  {
    field: 'startDate',
    type: FilterFieldType.Date,
    isSortable: true,
    title: 'Start Date',
  },
  {
    field: 'endDate',
    type: FilterFieldType.Date,
    isSortable: true,
    title: 'End Date',
  },
  {
    field: 'outstandingBalance',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Contractual Amount',
  },
  {
    field: 'effectiveBalance',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Effective Balance',
  },
] as const satisfies IFilter[]
